import invariant from 'invariant';
import queryString from 'query-string';

import { CurrentUser } from 'api/currentUser';
import { CurrentUser as GradFairsAppCurrentUser } from 'api/gradFairs';
import { isBrowser } from 'utils/browser';
import { configValue } from 'utils/config';
import { createCookie, deleteCookie, getCookieValue } from 'utils/cookies';
import { filterNulls, isNotEmpty } from 'utils/functional';
import { getItem, setItem } from 'utils/localStorage';
import { log } from 'utils/logging';
import * as mixpanelUtils from 'utils/mixpanel';
import trackError from 'utils/trackError';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateEvent = (eventName: string, properties: Record<string, any>) => {
  if (configValue('idealist', 'analytics', 'enforceValidEvents')) {
    invariant(
      !eventName.includes('_') && /^[A-Z0-9]/.test(eventName),
      `Invalid Analytics event name: "${eventName}"`
    );
    Object.keys(properties).forEach((name) => {
      invariant(
        /^[a-z0-9_]+$/.test(name),
        `Invalid Analytics property name: "${name}". Property names must be snake case`
      );
    });
  }
};

let registeredAbExperiments: string | null | undefined = null;
export const registerAbExperiments = (
  abExperiments:
    | Array<{
        id: string;
        created: string;
        bucket: string;
      }>
    | null
    | undefined
) => {
  registeredAbExperiments =
    abExperiments && isNotEmpty(abExperiments)
      ? `"${abExperiments.map((e) => `${e.id}=${e.bucket}`).join('"&"')}"`
      : null;

  if (registeredAbExperiments !== null) {
    mixpanelUtils.register({
      experiments: registeredAbExperiments,
    });
  } else {
    mixpanelUtils.unregister('experiments');
  }
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const windowDataLayerPush = (eventData: Record<string, any>) => {
  if (registeredAbExperiments !== null) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-param-reassign
    eventData = { ...eventData, experiments: registeredAbExperiments };
  }

  if (getCookieValue('_traceAnalytics')) {
    const trace = getItem('_analyticsTrace') || [];
    trace.push(eventData);
    setItem('_analyticsTrace', trace);
  }

  // @ts-expect-error ts-migrate(2339) FIXME: Property 'dataLayer' does not exist on type 'Windo... Remove this comment to see the full error message
  window.dataLayer.push(eventData);
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trackEvent = (eventName: string, ...args: any) => {
  let properties = {};
  let callback = null;

  if (args.length === 2) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    properties = args[0];
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    callback = args[1];
  } else if (args.length === 1) {
    if (typeof args[0] === 'function') {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      callback = args[0];
    } else {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      properties = args[0];
    }
  }

  validateEvent(eventName, properties);
  windowDataLayerPush({
    event: eventName,
    ...properties,
  });
  mixpanelUtils.track(eventName, properties, callback);
  log.info('Triggering analytics event', {
    event: eventName,
    ...properties,
  });
};
const EVENTS = {};
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trackEventOnce = (eventName: string, ...args: any) => {
  if (!isBrowser()) return;
  const key = `${eventName}${JSON.stringify(args)}${window.location.pathname}${
    window.location.search
  }`;

  if (!EVENTS[key]) {
    EVENTS[key] = true;
    trackEvent(eventName, ...args);
  }
};
export const trackPageView = (
  pageName: string,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any> = {}
) => {
  // Mixpanel assumes `id` refers to `userId`, so let's
  // rename to `listing_id` to keep things easier to parse.
  if (properties && properties.id) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-param-reassign
    properties.listing_id = properties.id;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-param-reassign
    delete properties.id;
  }

  trackEvent(`Page View: ${pageName}`, properties);
};
export const trackModalView = (
  modalName: string,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any> = {}
) => {
  trackEvent(`Modal View: ${modalName}`, properties);
};
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trackClicked = (name: string, ...args: any) => {
  trackEvent(`Clicked: ${name}`, ...args);
};
// only use for external links, not with react-router
export const setupLinkTracker =
  (
    name: string,
    properties: Record<string, any> = {} // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
  // eslint-disable-next-line consistent-return
  (el: Element | null | undefined) => {
    if (el) {
      if (!el.hasAttribute('href') || el.getAttribute('href') === '#') {
        throw new Error('Attempted to track link with no url');
      }

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-param-reassign
      properties.url = el.getAttribute('href');
      const eventName = `Clicked: ${name}`;
      validateEvent(eventName, properties);
      mixpanelUtils.trackLinks(el, eventName, properties);

      const clickHandler = () => {
        windowDataLayerPush({
          event: eventName,
          ...properties,
        });
        log.info('Triggering analytics event', {
          event: eventName,
          ...properties,
        });
      };

      el.addEventListener('click', clickHandler);
      return clickHandler;
    }
  };
export const getUserType = (user: CurrentUser | GradFairsAppCurrentUser) => {
  if (user.id) {
    if (user.permissions && user.permissions.includes('staff')) {
      return 'Staff';
    }

    if (user.orgs && user.orgs.length > 0) {
      let orgUserType = 'Org Admin';
      user.orgs.forEach((org) => {
        if (org.hasGradProgram) {
          orgUserType = 'Grad Admin';
        }
      });
      return orgUserType;
    }

    if (user.groups && user.groups.length > 0) {
      return 'Group Admin';
    }

    return 'Logged In User';
  }

  return 'Logged Out User';
};
export const trackLogin = (
  user: CurrentUser | GradFairsAppCurrentUser,
  method: string
) => {
  trackEvent('User Logged In', {
    user_id: user.id,
    method,
    user_type: getUserType(user),
  });

  if (user.id) {
    mixpanelUtils.register({
      user_id: user.id,
      user_type: getUserType(user),
    });
  } else {
    trackError('Missing user id after log in');
  }

  deleteCookie('justLoggedIn');
};
export const trackSignUp = (
  user: CurrentUser | GradFairsAppCurrentUser,
  method: string
) => {
  trackEvent('User Signed Up', {
    user_id: user.id,
    method,
    user_type: getUserType(user),
  });

  if (user.id) {
    mixpanelUtils.register({
      user_id: user.id,
      user_type: getUserType(user),
    });
  } else {
    trackError('Missing user id after sign up');
  }

  deleteCookie('justSignedUp');
};
export const trackLogout = () => {
  mixpanelUtils.resetUser();
  trackEvent('User Logged Out');
  deleteCookie('justLoggedOut');
};
export const bootstrapAnalytics = ({
  user,
}: {
  user: CurrentUser | GradFairsAppCurrentUser;
}) => {
  registerAbExperiments(user.abExperiments);

  if (getCookieValue('justSignedUp')) {
    const method = getCookieValue('justSignedUp');
    trackSignUp(user, method);
  }

  if (getCookieValue('justLoggedIn')) {
    const method = getCookieValue('justLoggedIn');
    trackLogin(user, method);
  }

  if (getCookieValue('justLoggedOut')) {
    trackLogout();
  }

  if (!user.id) {
    mixpanelUtils.unregister('user_id');
    mixpanelUtils.register({
      user_type: getUserType(user),
    });
  }

  const params = queryString.parse(window.location.search);
  const analyticsData = filterNulls({
    locale: USER_LOCALE,
    user_id: user.id,
    email_id: params.email_id,
    org_count: user.id ? user.orgs.length : null,
    user_type: getUserType(user),
  });
  const extraParams = filterNulls({
    utm_source: params.utm_source,
    utm_medium: params.utm_medium,
    utm_campaign: params.utm_campaign,
    utm_content: params.utm_content,
    utm_term: params.utm_term,
    sf_campaign_id: params.sf_campaign_id,
  });
  // These stored "extra" params are currently only used with the
  // donate form, so we want to avoid adding them elsewhere
  // like window.dataLayer.
  let storedExtraParams = {};

  if (getCookieValue('extraParams')) {
    storedExtraParams = JSON.parse(getCookieValue('extraParams'));
  }

  const extraParamsToStore = filterNulls({
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'utm_source' does not exist on type '{}'.
    utm_source: params.utm_source || storedExtraParams.utm_source,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'utm_medium' does not exist on type '{}'.
    utm_medium: params.utm_medium || storedExtraParams.utm_medium,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'utm_campaign' does not exist on type '{}... Remove this comment to see the full error message
    utm_campaign: params.utm_campaign || storedExtraParams.utm_campaign,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'utm_content' does not exist on type '{}'... Remove this comment to see the full error message
    utm_content: params.utm_content || storedExtraParams.utm_content,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'utm_term' does not exist on type '{}'.
    utm_term: params.utm_term || storedExtraParams.utm_term,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'sf_campaign_id' does not exist on type '... Remove this comment to see the full error message
    sf_campaign_id: params.sf_campaign_id || storedExtraParams.sf_campaign_id,
  });
  createCookie('extraParams', JSON.stringify(extraParamsToStore), 7);
  windowDataLayerPush({
    event: 'Full Page Load',
    // $FlowFixMe
    ...analyticsData,
    ...extraParams,
    logged_in_status: user.id ? 'Logged In' : 'Not Logged In',
  });
  mixpanelUtils.registerOnce(analyticsData);
};
